import React, { useState, useEffect } from 'react';
import { sessionsService } from '../../../shared/services/SessionsService';

interface SessionDetailsModalProps {
  sessionId: string;
  onClose: () => void;
}

interface FeedbackForm {
  id: string;
  sessionId: string;
  teacherId: string;
  teacherName: string;
  squadTitle: string;
  submittedAt: any;
  
  // Feedback form fields (these would depend on your actual feedback form structure)
  overallRating?: number;
  studentEngagement?: number;
  lessonEffectiveness?: number;
  technicalIssues?: string;
  classroomManagement?: number;
  timeManagement?: number;
  studentBehavior?: string;
  highlights?: string;
  challenges?: string;
  improvements?: string;
  additionalNotes?: string;
  wouldRecommend?: boolean;
  
  // Additional metadata
  location?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  duration?: number;
  attendanceNotes?: string;
}

const SessionDetailsModal: React.FC<SessionDetailsModalProps> = ({ sessionId, onClose }) => {
  const [feedbackForm, setFeedbackForm] = useState<FeedbackForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFeedbackForm = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const formData = await sessionsService.fetchFeedbackForm(sessionId);
        setFeedbackForm(formData);
      } catch (err) {
        console.error('Error loading feedback form:', err);
        setError('Failed to load feedback form details.');
      } finally {
        setLoading(false);
      }
    };

    loadFeedbackForm();
  }, [sessionId]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderRating = (rating?: number, label?: string) => {
    if (rating == null || rating <= 0) return 'Not provided';
    
    // Clamp rating between 0 and 5 to prevent negative or excessive values
    const clampedRating = Math.max(0, Math.min(5, rating));
    const filledStars = Math.max(0, Math.floor(clampedRating));
    const emptyStars = Math.max(0, 5 - filledStars);
    
    const stars = '★'.repeat(filledStars) + '☆'.repeat(emptyStars);
    return (
      <div className="rating-display">
        <span className="stars" style={{ color: '#f59e0b' }}>{stars}</span>
        <span className="rating-number">({clampedRating}/5)</span>
        {label && <span className="rating-label">{label}</span>}
      </div>
    );
  };

  const formatFieldName = (key: string): string => {
    // Convert camelCase to Title Case with spaces
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const hasValue = (value: any): boolean => {
    if (value == null || value === '') return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'number' && value <= 0) return false;
    return true;
  };

  const renderFieldValue = (key: string, value: any) => {
    // Handle booleans
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    
    // Handle timestamps
    if (value && typeof value.toDate === 'function') {
      return value.toDate().toLocaleString();
    }
    
    // Handle objects (like location, slack data)
    if (typeof value === 'object' && value !== null) {
      return <pre style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', fontSize: '13px', overflow: 'auto', border: '1px solid #e2e8f0', color: '#475569' }}>{JSON.stringify(value, null, 2)}</pre>;
    }
    
    // Handle numbers that might be ratings
    if (typeof value === 'number' && key.toLowerCase().includes('rating')) {
      return renderRating(value);
    }
    
    // Default: return as string
    return String(value);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div className="session-details-modal" style={{
        background: 'white',
        borderRadius: '16px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px 16px 0 0'
        }}>
          <h3 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: 'white' }}>Session Feedback</h3>
          <button onClick={onClose} style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white',
            fontSize: '28px',
            cursor: 'pointer',
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s'
          }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}>×</button>
        </div>

        {/* Modal Content - Scrollable */}
        <div style={{
          padding: '32px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
              <p style={{ color: '#64748b', fontSize: '16px' }}>Loading feedback...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <h4 style={{ color: '#ef4444', marginBottom: '8px' }}>Error</h4>
              <p style={{ color: '#64748b' }}>{error}</p>
            </div>
          ) : !feedbackForm ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
              <h4 style={{ color: '#1e293b', marginBottom: '8px' }}>No Feedback Available</h4>
              <p style={{ color: '#64748b', marginBottom: '16px' }}>No feedback form has been submitted for this session yet.</p>
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', fontSize: '14px', color: '#475569' }}>
                <strong>Session ID:</strong> {sessionId}
              </div>
            </div>
          ) : (
            <div>
              {/* Session Info Header */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                padding: '20px 24px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teacher</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>{feedbackForm.teacherName || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Squad</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>{feedbackForm.squadTitle || 'N/A'}</div>
                  </div>
                  {(feedbackForm as any).lessonId && (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Lesson</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Level {(feedbackForm as any).lessonId}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Submitted</div>
                    <div style={{ fontSize: '14px', color: '#475569' }}>{formatDate(feedbackForm.submittedAt)}</div>
                  </div>
                </div>
              </div>

              {/* Rating and Devices Side by Side */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                {/* Session Rating */}
                {(feedbackForm as any).sessionRating != null && hasValue((feedbackForm as any).sessionRating) && (
                  <div style={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#667eea',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Session Rating</div>
                    <div style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: '#1e293b'
                    }}>
                      {renderRating((feedbackForm as any).sessionRating)}
                    </div>
                  </div>
                )}

                {/* Devices Info - Consolidated */}
                {(hasValue((feedbackForm as any).devicesAtStart) || hasValue((feedbackForm as any).devicesAtEnd) || hasValue((feedbackForm as any).devicesNextRecipient)) && (
                  <div style={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#667eea',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Devices</div>
                    <div style={{ 
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: '#1e293b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      {hasValue((feedbackForm as any).devicesAtStart) && (
                        <>
                          <span style={{ color: '#64748b' }}>Start</span>
                          <span style={{ fontWeight: 600 }}>{(feedbackForm as any).devicesAtStart}</span>
                        </>
                      )}
                      {hasValue((feedbackForm as any).devicesAtStart) && hasValue((feedbackForm as any).devicesAtEnd) && (
                        <span style={{ color: '#64748b' }}>→</span>
                      )}
                      {hasValue((feedbackForm as any).devicesAtEnd) && (
                        <>
                          <span style={{ color: '#64748b' }}>End</span>
                          <span style={{ fontWeight: 600 }}>{(feedbackForm as any).devicesAtEnd}</span>
                        </>
                      )}
                      {hasValue((feedbackForm as any).devicesNextRecipient) && (hasValue((feedbackForm as any).devicesAtStart) || hasValue((feedbackForm as any).devicesAtEnd)) && (
                        <span style={{ color: '#64748b', margin: '0 4px' }}>|</span>
                      )}
                      {hasValue((feedbackForm as any).devicesNextRecipient) && (
                        <>
                          <span style={{ color: '#64748b' }}>Handoff:</span>
                          <span style={{ fontWeight: 600 }}>{(feedbackForm as any).devicesNextRecipient}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Feedback Fields - Only show fields with values, excluding special fields */}
              <div style={{ display: 'grid', gap: '20px' }}>
                {Object.entries(feedbackForm)
                  .filter(([key]) => !['id', 'sessionId', 'teacherId', 'teacherName', 'squadTitle', 'squadName', 'squadId', 'submittedAt', 'createdAt', 'formVersion', 'lessonId', 'sessionRating', 'overallRating', 'devicesAtStart', 'devicesAtEnd', 'devicesNextRecipient', 'devicesBrought', 'finalDeviceCount', 'initialDeviceCount', 'slack', 'deviceHandoff', 'otherIncidents', 'safetyIncidents', 'bugReportsText'].includes(key))
                  .filter(([_, value]) => hasValue(value))
                  .map(([key, value]) => (
                    <div key={key} style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '20px'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#667eea',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>{formatFieldName(key)}</div>
                      <div style={{
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: '#1e293b',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {renderFieldValue(key, value)}
                      </div>
                    </div>
                  ))}
              </div>

              {/* Show message if no feedback fields have values */}
              {Object.entries(feedbackForm)
                .filter(([key]) => !['id', 'sessionId', 'teacherId', 'teacherName', 'squadTitle', 'squadName', 'squadId', 'submittedAt', 'createdAt', 'formVersion'].includes(key))
                .filter(([_, value]) => hasValue(value)).length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px dashed #cbd5e1'
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '12px' }}>📝</div>
                  <p style={{ color: '#64748b', fontSize: '15px' }}>No detailed feedback was provided for this session.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '20px 32px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'flex-end',
          background: '#f8fafc',
          borderRadius: '0 0 16px 16px'
        }}>
          <button onClick={onClose} style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 32px',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
          }} onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
          }} onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
          }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailsModal;